import React from 'react';
import {
  ArrowRightIcon,
  Box,
  Button,
  ExternalLinkIcon,
  InfoIcon,
  Link,
  RefreshIcon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TablePagination,
  TableRow,
  TableToolbar,
  TableToolbarActions,
  Text,
  Tooltip,
  TooltipInteractiveWrapper,
} from '@razorpay/blade/components';
import { routePaths } from 'components/Routes/data';
import { StatusBadge } from 'components/SalaryComponents/components/StatusBadge';
import { TextWithDescription } from 'components/SalaryComponents/components/TextWithDescription';
import {
  helpArticles,
  perquisitesFiltersMap,
  salaryComponentParams
} from 'components/SalaryComponents/constants';
import { useDetailsView } from 'components/SalaryComponents/hooks';
import { PerquisitesComponentType } from 'components/SalaryComponents/types';
import { getTableItemCountLimits } from 'components/SalaryComponents/utils';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { useFilters } from 'hooks/useFilters';
import { usePagination } from 'hooks/usePagination';
import { useHistory } from 'react-router-dom';

const PerquisitesListView = ({
  data,
  isLoading,
  isRefreshing,
  isError,
}: {
  data: PerquisitesComponentType[];
  isLoading: boolean;
  isRefreshing: boolean;
  isError: boolean;
}) => {
  const history = useHistory();
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const { openDetailsView } = useDetailsView(salaryComponentParams.componentId);

  const isTableEmpty = data.length === 0;
  const errorString = isError ? 'Failed to load data' : '';
  const { currentFilters, isAnyFilterApplied } = useFilters({
    filtersMap: perquisitesFiltersMap,
    onParamsChange(params) {
      setCurrentPage(0);
    },
  });

  return (
    <>
      <Table
        data={{ nodes: data }}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        pagination={
          !isTableEmpty ? (
            <TablePagination
              currentPage={currentPage}
              showPageSizePicker={true}
              onPageChange={({ page }) => {
                setCurrentPage(page);
              }}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
              }}
              totalItemCount={data.length}
            />
          ) : undefined
        }
        toolbar={
          <TableToolbar title={getTableItemCountLimits(data.length, currentPage, itemsPerPage)}>
            <TableToolbarActions>
              <Button icon={RefreshIcon} variant={'tertiary'} onClick={() => {}} />
            </TableToolbarActions>
          </TableToolbar>
        }
        showBorderedCells={false}>
        {(salaryComponents) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>
                  <Text weight="semibold">Component name</Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Box display={'flex'} flexDirection={'row'} gap={'spacing.3'}>
                    <Text weight="semibold">Display name</Text>
                    <Tooltip
                      placement="top"
                      content={`The name that will appear in employee's payslip and Salary Register`}>
                      <TooltipInteractiveWrapper>
                        <InfoIcon marginTop={'spacing.1'} />
                      </TooltipInteractiveWrapper>
                    </Tooltip>
                  </Box>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Box marginX={'auto'}>
                    <Text weight="semibold">Status</Text>
                  </Box>
                </TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {salaryComponents.map((salaryComponent) => {
                return (
                  <TableRow
                    key={salaryComponent.id}
                    item={salaryComponent}
                    onClick={() => openDetailsView(salaryComponent.id)}
                    hoverActions={
                      <Button
                        variant="tertiary"
                        color="primary"
                        size="medium"
                        icon={ArrowRightIcon}
                        iconPosition="right"
                        onClick={() => openDetailsView(salaryComponent.id)}
                        testID={`view-details-button-${salaryComponent.id}`}>
                        View Details
                      </Button>
                    }>
                    <TableCell>
                      <TextWithDescription
                        componentName={salaryComponent.name}
                        description={salaryComponent.isPredefined ? 'Predefined' : 'Custom'}
                      />
                    </TableCell>
                    <TableCell>
                      <Text>
                        {salaryComponent.settings.behaviour.displayBehaviour?.displayName ??
                          salaryComponent.name}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Box marginX={'auto'}>
                        <StatusBadge isActive={salaryComponent.isActive} />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </>
        )}
      </Table>
      {isTableEmpty && !isLoading && !isRefreshing && !isError && (
        <EmptyListView
          title={
            isAnyFilterApplied
              ? 'No Search Results Found'
              : 'No Perquisites components created yet 🙈'
          }
          description={
            <Text color={'surface.text.gray.muted'} weight={'medium'} textAlign={'center'}>
              Click on Create Component button to create your first component
            </Text>
          }>
          <Box display={'flex'} flexDirection={'column'} gap={'spacing.4'} marginX={'auto'}>
            <Link
              size="medium"
              variant="anchor"
              href={helpArticles.learnMore} // link to be added
              icon={ExternalLinkIcon}
              iconPosition="right"
              target="_blank">
              Learn more about salary component
            </Link>
            <Box marginX={'auto'}>
              <Button
                size="medium"
                onClick={() => {
                  history.push(routePaths.salaryComponents.create.nonPayableBenefits.root);
                }}
                isFullWidth>
                Create Perquisites Component
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
      {isError && (
        <EmptyListView
          title={`There was an error while fetching the data for ${
            currentFilters.status ?? 'all'
          } perquisites components. `}
          description={errorString ? `Error: ${errorString}` : null}>
          <Box display={'flex'} flexDirection={'column'} gap={'spacing.4'} marginX={'auto'}>
            <Link
              size="medium"
              variant="anchor"
              href={helpArticles.learnMore} // link to be added
              icon={ExternalLinkIcon}
              iconPosition="right"
              target="_blank">
              Learn more about salary component
            </Link>
            <Box marginX={'auto'}>
              <Button
                size="medium"
                onClick={() => {}}
                isFullWidth
                iconPosition="left"
                icon={RefreshIcon}>
                Retry
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
    </>
  );
};

export default PerquisitesListView;
